import { useState, useEffect, useMemo, Fragment } from 'react';
import { Loader2, TrendingUp, Euro, ChevronRight, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { api } from '@/lib/api';
import type { VerbruikRegel } from '@/lib/api-types';

/**
 * Het maandoverzicht per praktijk.
 *
 * GET /api/admin/usage levert `months` als lijst; get-practice-usage gaf een object
 * met maandsleutels. Een lijst is hier beter: de volgorde staat vast en de server
 * bepaalt hem, in plaats van dat elk scherm de sleutels zelf moet sorteren.
 */
interface PracticeMonthly {
  practiceId: string;
  practiceName: string;
  agbCode: string;
  afasId: string | null;
  months: Array<{ month: string; purchase: number; selling: number; entries: number }>;
  totals: { purchase: number; selling: number; entries: number };
}

type DetailedLog = VerbruikRegel;

interface Node {
  key: string;
  label: string;
  purchase: number;
  selling: number;
  children: Record<string, Node>;
  sortTime?: number;
}

function ensure(map: Record<string, Node>, key: string, label: string): Node {
  if (!map[key]) map[key] = { key, label, purchase: 0, selling: 0, children: {} };
  return map[key];
}

const USAGE_TYPE_LABELS: Record<string, string> = {
  speech_recording: 'Spraakopname',
  soep_transcription: 'SOEP Samenvatting',
  translation: 'Vertaling',
  icpc_suggestion: 'ICPC Suggestie',
  patient_summary: 'Patiëntsamenvatting (PDF)',
  acs_call: 'Telefoongesprek (ACS)',
};

function euro(n: number) {
  return `€${n.toFixed(2).replace('.', ',')}`;
}

function formatMonth(monthStr: string): string {
  const [year, month] = monthStr.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
}

function formatLogLabel(log: DetailedLog): string {
  const dt = new Date(log.createdAt).toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts: string[] = [dt];
  if (log.seconds != null && Number(log.seconds) > 0) parts.push(`${Number(log.seconds).toFixed(0)}s`);
  if (log.model) parts.push(String(log.model));
  return parts.join(' · ');
}

export function FinancialResultTab() {
  const [practices, setPractices] = useState<PracticeMonthly[]>([]);
  const [logs, setLogs] = useState<DetailedLog[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const [overzicht, regels] = await Promise.all([
          api.get<{ practices: PracticeMonthly[] }>('/admin/usage'),
          api.get<{ entries: VerbruikRegel[] }>('/admin/usage/entries', {
            query: { limit: 2000 },
          }),
        ]);
        setPractices(overzicht.practices);
        setLogs(regels.entries);
      } catch {
        setPractices([]);
        setLogs([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // month -> practice -> user -> type tree
  const tree = useMemo(() => {
    const root: Record<string, Node> = {};
    for (const log of logs) {
      const month = String(log.createdAt).slice(0, 7);
      const monthNode = ensure(root, month, formatMonth(month));
      const pKey = `${month}|${log.practiceId || 'unknown'}`;
      const practiceNode = ensure(monthNode.children, pKey, log.practiceName || 'Onbekende praktijk');
      const uKey = `${pKey}|${log.practiceUserId || log.userName || 'unknown'}`;
      const userNode = ensure(practiceNode.children, uKey, log.userName || 'Onbekende gebruiker');
      const tKey = `${uKey}|${log.usageType || 'unknown'}`;
      const typeNode = ensure(
        userNode.children,
        tKey,
        USAGE_TYPE_LABELS[log.usageType] || log.usageType || 'Onbekend type'
      );
      const logNode = ensure(typeNode.children, `${tKey}|${log.id}`, formatLogLabel(log));
      logNode.sortTime = new Date(log.createdAt).getTime();
      const purchase = log.purchaseCost ?? 0;
      const selling = log.sellingCost ?? 0;
      for (const n of [monthNode, practiceNode, userNode, typeNode, logNode]) {
        n.purchase += purchase;
        n.selling += selling;
      }
    }
    return Object.values(root).sort((a, b) => b.key.localeCompare(a.key));
  }, [logs]);

  const monthlyTotals = useMemo(() => {
    const totals: Record<string, { purchase: number; selling: number }> = {};
    for (const p of practices) {
      // months is een lijst; get-practice-usage gaf hier een object met
      // maandsleutels, waarna elk scherm zelf moest sorteren.
      for (const maand of p.months) {
        if (!totals[maand.month]) totals[maand.month] = { purchase: 0, selling: 0 };
        totals[maand.month].purchase += maand.purchase;
        totals[maand.month].selling += maand.selling;
      }
    }
    return Object.entries(totals)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([month, data]) => ({
        month,
        label: formatMonth(month),
        purchase: data.purchase,
        selling: data.selling,
        profit: data.selling - data.purchase,
      }));
  }, [practices]);

  const grandTotals = useMemo(() => {
    return monthlyTotals.reduce(
      (acc, m) => ({
        purchase: acc.purchase + m.purchase,
        selling: acc.selling + m.selling,
        profit: acc.profit + m.profit,
      }),
      { purchase: 0, selling: 0, profit: 0 }
    );
  }, [monthlyTotals]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderRow = (node: Node, depth: number) => {
    const hasChildren = Object.keys(node.children).length > 0;
    const isOpen = !!expanded[node.key];
    const profit = node.selling - node.purchase;
    const margin = node.selling > 0 ? (profit / node.selling) * 100 : 0;
    const children = Object.values(node.children).sort((a, b) => {
      // Detailniveau (individuele logs): nieuwste bovenaan, oudste onderaan
      if (a.sortTime != null && b.sortTime != null) return b.sortTime - a.sortTime;
      return b.selling - a.selling;
    });
    return (
      <Fragment key={node.key}>
        <tr
          className={`border-t hover:bg-muted/50 ${hasChildren ? 'cursor-pointer' : ''}`}
          onClick={hasChildren ? () => toggle(node.key) : undefined}
        >
          <td className="p-3" style={{ paddingLeft: `${12 + depth * 20}px` }}>
            <span className="inline-flex items-center gap-1.5">
              {hasChildren ? (
                isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
              ) : (
                <span className="inline-block w-3.5" />
              )}
              <span className={depth === 0 ? 'capitalize font-medium' : ''}>{node.label}</span>
            </span>
          </td>
          <td className="p-3 text-right text-muted-foreground">{euro(node.purchase)}</td>
          <td className="p-3 text-right">{euro(node.selling)}</td>
          <td className={`p-3 text-right font-medium ${profit >= 0 ? 'text-green-600' : 'text-destructive'}`}>
            {euro(profit)}
          </td>
          <td className="p-3 text-right text-muted-foreground">{margin.toFixed(0)}%</td>
        </tr>
        {isOpen && children.map((child) => renderRow(child, depth + 1))}
      </Fragment>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Financieel Resultaat
        </CardTitle>
        <CardDescription>Overzicht van kosten, omzet en winst per maand</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Grand totals */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Euro className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Totale Kosten</span>
            </div>
            <p className="text-2xl font-bold">€{grandTotals.purchase.toFixed(2).replace('.', ',')}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Euro className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Totale Omzet</span>
            </div>
            <p className="text-2xl font-bold">€{grandTotals.selling.toFixed(2).replace('.', ',')}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Totale Winst</span>
            </div>
            <p className={`text-2xl font-bold ${grandTotals.profit >= 0 ? 'text-green-600' : 'text-destructive'}`}>
              €{grandTotals.profit.toFixed(2).replace('.', ',')}
            </p>
          </div>
        </div>

        {/* Monthly table with drill-down */}
        {tree.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nog geen financiële data beschikbaar.</p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <p className="px-3 py-2 text-xs text-muted-foreground bg-muted/40">
              Klik door van maand → praktijk → gebruiker → type verbruik → losse registratie.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-3 font-medium">Maand</th>
                  <th className="text-right p-3 font-medium">Kosten (inkoop)</th>
                  <th className="text-right p-3 font-medium">Omzet (verkoop)</th>
                  <th className="text-right p-3 font-medium">Winst</th>
                  <th className="text-right p-3 font-medium">Marge</th>
                </tr>
              </thead>
              <tbody>
                {tree.map((m) => renderRow(m, 0))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
